import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api';

interface Agent { name: string; status: string; restarts: number; cpu: number; memory: number; }

const AGENT_KEYS: Record<string, string> = {
  'kvass-agent': 'agent.kvass_tester',
  'changelog-agent': 'agent.changelog',
  'testdata-agent': 'agent.testdata',
  'testdekning-agent': 'agent.coverage',
};

export default function Dashboard() {
  const { t } = useTranslation();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [logs, setLogs] = useState<Record<string, string[]>>({});
  const [modal, setModal] = useState<{ name: string; lines: string[] } | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/agents');
        const agentList = (Array.isArray(res.data) ? res.data : []).filter((a: Agent) => a.name !== 'dashboard-backend' && a.name !== 'test-agent');
        setAgents(agentList);
        const logData: Record<string, string[]> = {};
        for (const agent of agentList) {
          try {
            const logRes = await api.get(`/logs/${agent.name}`);
            logData[agent.name] = Array.isArray(logRes.data.lines) ? logRes.data.lines : [];
          } catch { logData[agent.name] = []; }
        }
        setLogs(logData);
      } catch {}
    };
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, []);

  const openModal = (agent: Agent) => {
    const key = AGENT_KEYS[agent.name] || agent.name;
    setModal({ name: t(key), lines: (logs[agent.name] || []).slice(-25).reverse() });
  };

  const LogLine = ({ line, bright }: { line: string; bright?: boolean }) => (
    <div className="font-mono truncate" style={{ fontSize: '11px', color: bright ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.35)', lineHeight: '1.8' }}>
      <span style={{ color: '#db12ca' }}>{line.match(/\[.*?\]/)?.[0]}</span>
      {line.replace(/\[.*?\]/, '')}
    </div>
  );

  const onlineCount = agents.filter(a => a.status === 'online').length;

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', paddingBottom: '16px' }}>

      {/* Side decoration */}
      <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '3px', background: 'linear-gradient(180deg, transparent, rgba(185,106,242,0.4) 30%, rgba(219,18,202,0.3) 70%, transparent)', pointerEvents: 'none' }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '3px', background: 'linear-gradient(180deg, transparent, rgba(185,106,242,0.4) 30%, rgba(219,18,202,0.3) 70%, transparent)', pointerEvents: 'none' }} />

      <h1 className="text-2xl font-medium mb-5" style={{ color: '#fff' }}>
        {t('dashboard.title')}
        <span className="inline-block w-0.5 h-6 ml-1 rounded align-middle animate-pulse" style={{ background: '#db12ca' }} />
      </h1>
      <div style={{ height: '1.5px', borderRadius: '99px', background: 'linear-gradient(90deg, #b96af2, #db12ca 50%, transparent)', marginBottom: '20px', opacity: 0.85 }} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {agents.map(agent => {
          const key = AGENT_KEYS[agent.name] || agent.name;
          return (
            <div key={agent.name} className="rounded-2xl p-5 relative overflow-hidden transition-all duration-200"
              style={{ background: '#2c2840', border: '1px solid rgba(185,106,242,0.1)' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = 'rgba(185,106,242,0.3)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'rgba(185,106,242,0.1)'}>
              <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '1px', background: 'linear-gradient(90deg, transparent, #b96af2, #db12ca, transparent)', opacity: 0.55 }} />

              <div className="flex justify-between items-center mb-4">
                <span className="font-medium text-white">{t(key)}</span>
                <span className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-full"
                  style={{ background: agent.status === 'online' ? 'rgba(30,0,40,0.9)' : 'rgba(40,10,10,0.9)', color: agent.status === 'online' ? '#b96af2' : '#ff7070', border: `1px solid ${agent.status === 'online' ? 'rgba(185,106,242,0.28)' : 'rgba(255,100,100,0.2)'}` }}>
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0" style={{ background: agent.status === 'online' ? '#b96af2' : '#ff7070', boxShadow: agent.status === 'online' ? '0 0 5px #b96af2' : 'none' }} />
                  {agent.status === 'online' ? t('dashboard.online') : t('dashboard.offline')}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-4">
                {[['CPU', `${agent.cpu}%`], ['RAM', `${Math.round(agent.memory / 1024 / 1024)} MB`], [t('dashboard.restarts'), `${agent.restarts}`]].map(([label, value]) => (
                  <div key={label} className="rounded-xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <div className="mb-1 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.26)', fontSize: '10px' }}>{label}</div>
                    <div className="text-base font-medium text-white">{value}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-xl px-3 py-2.5 cursor-pointer transition-all duration-150"
                style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid transparent' }}
                onClick={() => openModal(agent)}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = 'rgba(185,106,242,0.2)'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'transparent'}>
                <div className="flex justify-between items-center mb-2">
                  <div className="uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.22)', fontSize: '10px' }}>{t('dashboard.logs')}</div>
                  <div style={{ fontSize: '10px', color: 'rgba(185,106,242,0.6)' }}>↗ {t('dashboard.view_all')}</div>
                </div>
                {logs[agent.name]?.slice(-3).reverse().map((line, i) => (
                  <LogLine key={i} line={line} bright={i === 0} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-5 px-1" style={{ borderTop: '1px solid rgba(185,106,242,0.1)', paddingTop: '14px' }}>
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#b96af2', boxShadow: '0 0 5px #b96af2' }} />
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>{t('footer.status')}</span>
        </div>
        <div className="flex items-center gap-4">
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.2)' }}>
            {onlineCount}/{agents.length} online
          </span>
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.2)' }}>{t('footer.version')}</span>
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.6)' }}
          onClick={() => setModal(null)}>
          <div className="rounded-2xl p-6 w-full max-w-2xl mx-4"
            style={{ background: '#2a2540', border: '1px solid rgba(185,106,242,0.25)', boxShadow: '0 16px 48px rgba(0,0,0,0.5)', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <span className="font-medium text-white">{modal.name}</span>
                <span className="ml-2 uppercase tracking-wider" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>— {t('dashboard.last_logs')}</span>
              </div>
              <button onClick={() => setModal(null)}
                className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-all duration-150"
                style={{ color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent' }}
                onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.color = '#fff'; el.style.borderColor = 'rgba(185,106,242,0.5)'; el.style.background = 'rgba(185,106,242,0.12)'; }}
                onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.color = 'rgba(255,255,255,0.4)'; el.style.borderColor = 'rgba(255,255,255,0.1)'; el.style.background = 'transparent'; }}>
                {t('dashboard.close')}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ height: '1px', background: 'rgba(185,106,242,0.15)', marginBottom: '16px' }} />
            <div className="overflow-y-auto rounded-xl px-4 py-3" style={{ background: 'rgba(0,0,0,0.25)' }}>
              {modal.lines.length ? modal.lines.map((line, i) => (
                <LogLine key={i} line={line} bright={i === 0} />
              )) : <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>{t('dashboard.no_logs_modal')}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
